import {
  CheckCircleFilled,
  ExclamationCircleFilled,
  QuestionCircleFilled,
} from "@ant-design/icons";
import { lazy, Suspense, useMemo, useState } from "react";

import type { CityLocation, WeatherImpactRow } from "../domain/types";
import { EstimateQuality } from "./EstimateQuality";
import { WeatherList } from "./WeatherList";

const ImpactCharts = lazy(() => import("./ImpactCharts").then((module) => ({ default: module.ImpactCharts })));

interface CityInspectorProps {
  city: CityLocation;
  currentHour: number;
  rows: WeatherImpactRow[];
  selectedRowId: string | null;
  onRowSelect: (row: WeatherImpactRow) => void;
}

const summaryIcons = {
  gap: ExclamationCircleFilled,
  aligned: CheckCircleFilled,
  insufficient: QuestionCircleFilled,
};

const removedGenericWarnings = new Set([
  "当前信息不支持可靠比较",
  "数据时效、字段或置信度不足，无法可靠比较",
]);

export function CityInspector({ city, currentHour, rows, selectedRowId, onRowSelect }: CityInspectorProps) {
  const [mobileForecastOpen, setMobileForecastOpen] = useState(false);
  const chartRows = useMemo(() => rows.filter((_, index) => index % 3 === 0).slice(0, 24), [rows]);
  const currentRow = rows[Math.min(rows.length - 1, Math.max(0, Math.floor(currentHour)))] ?? rows[0];
  const SummaryIcon = currentRow ? summaryIcons[currentRow.comparison.status] : QuestionCircleFilled;

  return (
    <div className={`city-inspector glass-panel${mobileForecastOpen ? " city-inspector--mobile-forecast" : ""}`}>
      <span aria-hidden className="sheet-handle" />
      <header className="city-inspector__header">
        <h2>{city.name} · 未来 72 小时</h2>
        {rows.length ? (
          <button
            aria-label={mobileForecastOpen ? "返回影响概览" : "查看 72 小时天气"}
            aria-pressed={mobileForecastOpen}
            className="city-inspector__mobile-toggle"
            onClick={() => setMobileForecastOpen((open) => !open)}
            type="button"
          >
            {mobileForecastOpen ? "返回概览" : "72 小时天气"}
          </button>
        ) : null}
      </header>
      {rows.length && currentRow ? (
        <>
          <Suspense fallback={<div className="charts-loading">正在加载风雨对比图</div>}>
            <ImpactCharts rows={chartRows} />
          </Suspense>
          <section className={`impact-summary impact-summary--${currentRow.comparison.status}`} aria-label="当前时段影响对比">
            <div className="impact-summary__time">当前时段 {formatRange(currentRow.startTime, currentRow.endTime)}</div>
            <dl>
              <div><dt>台风降雨</dt><dd><EstimateQuality level="low" subject="降雨" />{currentRow.impactRainMm ? `${currentRow.impactRainMm[0]}–${currentRow.impactRainMm[1]}mm` : "—"}</dd></div>
              <div><dt>台风风力</dt><dd><EstimateQuality level={currentRow.confidence} subject="风力" />{currentRow.impactWindScale ? `${currentRow.impactWindScale[0]}–${currentRow.impactWindScale[1]}级` : "—"}</dd></div>
              <div><dt>综合预报</dt><dd>{currentRow.weatherText} · {currentRow.windScale}级</dd></div>
            </dl>
            {!removedGenericWarnings.has(currentRow.comparison.reason) ? (
              <p className="impact-summary__message"><SummaryIcon aria-hidden />{currentRow.comparison.reason}</p>
            ) : null}
          </section>
          <WeatherList rows={rows} selectedRowId={selectedRowId} onRowSelect={onRowSelect} />
        </>
      ) : <div className="panel-empty panel-empty--large">当前暂无可展示的城市天气或台风影响</div>}
    </div>
  );
}

function formatRange(start: string, end: string) {
  const formatter = new Intl.DateTimeFormat("zh-CN", {
    timeZone: "Asia/Shanghai", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false,
  });
  return `${formatter.format(new Date(start))} – ${formatter.format(new Date(end))}`;
}
